import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FixCheck, FixCheckConfirm } from '@testflow/shared';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isFixCheckStaleError, mapApiError } from '../../lib/errors.js';
import { myDefectsApi } from '../../lib/runs-api.js';
import { toast } from '../../lib/toast.js';
import { useUrlState } from '../../lib/use-url-state.js';

// =================================================================================================
// #194: Die Fehlerprüfungen von „Meine Tests" an einer Stelle — Abfrage, Deeplink `?fixcheck=` und
// die Bestätigung („Behoben"/„Nicht behoben"). Aus my-tests-page.tsx herausgelöst (Nachtrag 14.10.),
// als die Aktion von der Karte ins Detail rechts wanderte. Die AUSWAHL bleibt bei der Seite; der Hook
// meldet nur, wann ausgewählt werden soll (`onDeeplink`) und wann eine Bestätigung durch ist
// (`onConfirmed`).
// =================================================================================================

/**
 * #194: nächste Fehlerprüfung nach dem Bestätigen — in der Reihenfolge der Liste VOR dem Neuladen
 * (`before`), ab der Stelle hinter der bestätigten, dann von vorn. Ziel ist nur, was das Neuladen
 * noch liefert (`after`, schon auf Plan und Suche gefiltert); tauchen dort nur neue Prüfungen auf,
 * ist die erste davon dran.
 */
export function nextFixCheck(
  before: FixCheck[],
  after: FixCheck[],
  confirmedId: string,
): FixCheck | null {
  const start = before.findIndex((c) => c.defect_id === confirmedId);
  // Wie nextAfterSubmit: fehlt die bestätigte (-1), ab Listenanfang — ohne `slice(0, -1)`.
  const rotated = [...before.slice(start + 1), ...before.slice(0, Math.max(start, 0))];
  const remaining = new Map(after.map((c) => [c.defect_id, c]));
  remaining.delete(confirmedId);
  for (const candidate of rotated) {
    const hit = remaining.get(candidate.defect_id);
    if (hit) return hit;
  }
  return remaining.values().next().value ?? null;
}

export function useFixChecks({
  projectId,
  canConfirmFix,
  userLoaded,
  selectedPlanId,
  selectPlan,
  onDeeplink,
  onConfirmed,
}: {
  projectId: string;
  /** `defect.confirm_fix` — ohne das Recht keine Abfrage. Wer Prüfer ist, entscheidet der Server. */
  canConfirmFix: boolean;
  /** Nutzer geladen: erst dann heißt `canConfirmFix === false` wirklich „kein Recht". */
  userLoaded: boolean;
  selectedPlanId: string | null;
  selectPlan: (planId: string) => void;
  /** Deeplink aufgelöst: diese Karte auswählen (der Plan ist dann schon gewählt). */
  onDeeplink: (defectId: string) => void;
  /**
   * Bestätigung durch UND neu geladen: `before` ist die sichtbare Liste beim Absenden, `fresh` die
   * neu geladene (alle Pläne). Die Seite wählt daraus die nächste Karte.
   */
  onConfirmed: (check: FixCheck, before: FixCheck[], fresh: FixCheck[]) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Offene Fehlerprüfungen über ALLE aktiven Pläne (ein Abruf je Projekt) — gezeigt werden nur die
  // des gewählten Plans (B5/E7); der Deeplink braucht die anderen, um den Plan zu wechseln.
  const query = useQuery({
    queryKey: ['fix-checks', projectId],
    queryFn: () => myDefectsApi.fixChecks(projectId),
    enabled: canConfirmFix && Boolean(projectId),
  });
  const allFixChecks = query.data;
  const planFixChecks = (allFixChecks ?? []).filter((c) => c.test_plan_id === selectedPlanId);

  // Deeplink aus der Glocke `retest_due` mit `fix_check` (`/my-tests?fixcheck=<defect_id>`,
  // notification-deeplink.ts). Wie `?case=`: EINMAL ausgewertet, dann entfernt — gefunden oder
  // nicht. Liegt die Prüfung in einem anderen aktiven Plan, wird dieser gewählt; die Karte wird
  // AUSGEWÄHLT (Nachtrag 14.10.: nicht nur hervorgehoben) und scrollt sich selbst in den Blick.
  // Final-Review: Ausgewertet wird NUR gegen Daten, die nach dem Auftauchen des Parameters geholt
  // wurden (`refetch()`, dann dessen Ergebnis). Beim Glocken-Klick bleibt die Seite gemountet, bei
  // Rückkehr innerhalb der Cache-Zeit liegt die Liste schon vor — beide Male von VOR „Retest
  // bereit". Ohne das Recht kommt die Abfrage nie — dann den Parameter gleich verwerfen.
  const [fixCheckParam, setFixCheckParam] = useUrlState('fixcheck', '');
  const refetch = query.refetch;
  // Plan-Stand, Setter und Rückmeldungen über eine Ref: der Effekt soll nur auf Parameter, Recht und
  // Projekt reagieren — ein neuer Lauf bräche den laufenden Abruf ab und holte erneut (der Setter aus
  // useSearchParams wechselt mit jeder URL-Änderung). Die Mutation liest hier ebenfalls die Stände des
  // LETZTEN Renders (Plan, Suche, Playlist der Seite).
  const latest = useRef({ selectedPlanId, selectPlan, onDeeplink, onConfirmed, setFixCheckParam });
  latest.current = { selectedPlanId, selectPlan, onDeeplink, onConfirmed, setFixCheckParam };
  useEffect(() => {
    if (!fixCheckParam) return;
    if (userLoaded && !canConfirmFix) {
      latest.current.setFixCheckParam('');
      return;
    }
    if (!canConfirmFix || !projectId) return;
    let cancelled = false;
    void refetch().then((result) => {
      // Ohne Daten (Abruf fehlgeschlagen) bleibt der Parameter stehen; die Liste zeigt dann ihren
      // eigenen Fehlerzustand mit „Erneut versuchen".
      if (cancelled || result.isError || !result.data) return;
      const {
        selectedPlanId: current,
        selectPlan: choose,
        onDeeplink: select,
        setFixCheckParam: clear,
      } = latest.current;
      const hit = result.data.find((c) => c.defect_id === fixCheckParam);
      if (hit) {
        // Erst der Plan (sein Wechsel leert die Auswahl), dann die Karte.
        if (hit.test_plan_id !== current) choose(hit.test_plan_id);
        select(hit.defect_id);
      }
      clear('');
    });
    return () => {
      cancelled = true;
    };
  }, [fixCheckParam, userLoaded, canConfirmFix, projectId, refetch]);

  // „Behoben" / „Nicht behoben". Fehler meldet die Mutation selbst (errorToast: false): der 409
  // `fix_check_not_open` (jemand hat den Defect inzwischen anders bearbeitet) bzw. 404 einer
  // abgelaufenen Vertretung ist kein Fehler des Prüfers, sondern ein veralteter Stand — Hinweis statt
  // Fehler-Toast, und die Karten neu laden. Fällt die Karte dabei weg, hebt die Seite die Auswahl auf.
  const mutation = useMutation({
    mutationFn: (v: { check: FixCheck; body: FixCheckConfirm; before: FixCheck[] }) =>
      myDefectsApi.confirmFix(projectId, v.check.defect_id, v.body),
    meta: { errorToast: false },
    // Das Neuladen wird ABGEWARTET: die Mutation bleibt `pending`, bis es die Karte entfernt hat.
    // Sonst stand das Detail dazwischen wieder bedienbar da, und ein zweiter Klick schickte dieselbe
    // Bestätigung noch einmal (409).
    onSuccess: async (_result, v) => {
      toast.success(
        t(v.body.fixed ? 'fixCheck.toastClosed' : 'fixCheck.toastBack', { id: v.check.human_id }),
      );
      await Promise.all(
        [
          ['fix-checks', projectId],
          // Der Defect wechselt den Status — „Meine Fehler" unter der Ausführung und die Worklist
          // „von mir gemeldet" zeigen ihn.
          ['my-defects', projectId],
          ['my-reported', projectId],
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
      latest.current.onConfirmed(
        v.check,
        v.before,
        queryClient.getQueryData<FixCheck[]>(['fix-checks', projectId]) ?? [],
      );
      // Defect-Körper und Verlauf des bestätigten Defects sind veraltet. Nur als veraltet markieren,
      // nicht sofort neu laden: das Detail springt gerade weiter, und eine Vertretung darf den Defect
      // nach dem Statuswechsel womöglich nicht mehr lesen (404 im noch sichtbaren Detail).
      for (const queryKey of [
        ['my-defect', projectId, v.check.defect_id],
        ['my-defect-comments', projectId, v.check.defect_id],
      ]) {
        void queryClient.invalidateQueries({ queryKey, refetchType: 'none' });
      }
    },
    onError: (err) => {
      // #194 Final-Review: auch 404 (Vertretung abgelaufen) ist ein veralteter Stand, kein Fehler.
      if (isFixCheckStaleError(err)) {
        toast.info(t('fixCheck.stale'));
        void queryClient.invalidateQueries({ queryKey: ['fix-checks', projectId] });
        return;
      }
      toast.error(mapApiError(t, err));
    },
  });

  return {
    query,
    allFixChecks,
    planFixChecks,
    /** Bestätigen; `before` = die beim Absenden sichtbare Kartenliste (für den Sprung danach). */
    confirm: (check: FixCheck, body: FixCheckConfirm, before: FixCheck[]) =>
      mutation.mutate({ check, body, before }),
    /** Defect, dessen Bestätigung gerade läuft — nur dessen Detail ist gesperrt. */
    pendingId: mutation.isPending ? (mutation.variables?.check.defect_id ?? null) : null,
    /**
     * #194 Final-Review: die Prüfung, deren Bestätigung gerade läuft. Nach dem Neuladen fehlt sie in
     * der Liste, die Auswahl springt aber erst, wenn ALLE Abfragen zurück sind — bis dahin zeigt die
     * Seite ihr (gesperrtes) Detail aus diesem Stand weiter.
     */
    pendingCheck: mutation.isPending ? (mutation.variables?.check ?? null) : null,
  };
}
